/**
 * =============================================================================
 * MACHINE FINGERPRINT — Stable, deterministic machine identifier
 * =============================================================================
 * In Electron: requests the ID from main process via IPC (uses Node os/crypto).
 * In web mode : uses a UUID persisted in localStorage (web-compatible fallback).
 *
 * The ID is NOT a globally unique device serial — it is a SHA-256 hash of
 * stable OS signals, giving a consistent identifier per machine/user account
 * while being hard to trivially clone.
 */

const STORAGE_KEY = 'wf_machine_fingerprint';

/**
 * Returns the machine fingerprint string.
 * Call this once at startup and cache the result.
 */
export async function getMachineFingerprint(): Promise<string> {
  // ── Electron path ────────────────────────────────────────────────────────
  const electronAPI = (window as any).electronAPI;
  if (electronAPI?.getMachineId) {
    try {
      const id = await electronAPI.getMachineId();
      if (typeof id === 'string' && id.length > 0) {
        return id;
      }
    } catch {
      // fall through to browser path
    }
  }

  // ── Browser / fallback path ───────────────────────────────────────────────
  return getBrowserFingerprint();
}

/**
 * Browser-based fingerprint: a UUID that persists across sessions.
 * Less stable than the Electron version (cleared with browser data) but
 * provides a unique-per-install identifier for web-hosted mode.
 */
function getBrowserFingerprint(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length >= 32) {
      return stored;
    }

    const generated = generateBrowserId();
    localStorage.setItem(STORAGE_KEY, generated);
    return generated;
  } catch {
    // If localStorage is unavailable return a session-scoped ID
    return generateBrowserId();
  }
}

/**
 * Generates a deterministic-ish browser fingerprint by hashing navigator
 * signals together with a random salt stored in localStorage.
 */
function generateBrowserId(): string {
  const signals = [
    navigator.userAgent ?? '',
    navigator.language ?? '',
    String(screen.width ?? 0),
    String(screen.height ?? 0),
    String(new Date().getTimezoneOffset()),
    // Random entropy stored per-install
    crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
  ].join('|');

  // Use SubtleCrypto if available for a proper SHA-256 hash
  // but since this is called synchronously in the fallback we use a fast
  // non-crypto djb2 hash instead (SubtleCrypto is async, handled separately)
  return 'wf-web-' + djb2Hash(signals).toString(16).padStart(16, '0');
}

function djb2Hash(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    hash = hash >>> 0; // keep 32-bit unsigned
  }
  return hash;
}
